/**
 * Toast filter utility to suppress unhelpful raw error messages.
 *
 * Wraps react-toastify's toast.error to:
 * 1. Suppress raw "Network Error" messages (from Axios XHR failures)
 * 2. Deduplicate identical error messages within a short time window
 * 3. Provide a user-friendly fallback for known raw error patterns
 *
 * Usage: import and call once at app startup (e.g., in index.js or getApp.js)
 *   import { installToastFilter } from './utils/toastFilter';
 *   installToastFilter();
 */
import { toast } from 'react-toastify';

// Raw error messages that should never be shown to users as-is
const SUPPRESSED_MESSAGES = [
  'network error',         // Axios XHR failure
  'failed to fetch',       // Fetch API failure
  'load failed',           // Safari fetch failure
  'the operation was aborted', // AbortController
  'canceled',              // Axios cancel
  'aborted',               // Generic abort
  'timeout',               // Timeout (no context for user)
];

// Deduplication window (ms) — suppress identical messages within this period
const DEDUP_WINDOW_MS = 3000;
const recentToasts = new Map();

/**
 * Check if a toast message should be suppressed.
 * @param {string|React.ReactNode} content - The toast content
 * @returns {boolean} true if the toast should be blocked
 */
const shouldSuppressToast = (content) => {
  if (typeof content !== 'string') return false;
  const lower = content.toLowerCase().trim();

  // Suppress raw network error messages (unhelpful to users)
  if (SUPPRESSED_MESSAGES.some((msg) => lower === msg)) {
    console.debug('[ToastFilter] Suppressed raw error toast:', content);
    return true;
  }

  return false;
};

/**
 * Check if a toast is a duplicate of a recent toast.
 * @param {string|React.ReactNode} content - The toast content
 * @returns {boolean} true if it's a duplicate
 */
const isDuplicate = (content) => {
  if (typeof content !== 'string') return false;

  const now = Date.now();
  const key = content.toLowerCase().trim();

  // Clean expired entries
  for (const [k, ts] of recentToasts) {
    if (now - ts > DEDUP_WINDOW_MS) recentToasts.delete(k);
  }

  if (recentToasts.has(key)) {
    console.debug('[ToastFilter] Deduplicated toast:', content);
    return true;
  }

  recentToasts.set(key, now);
  return false;
};

/**
 * Install the toast filter by wrapping toast.error.
 * Safe to call multiple times (idempotent).
 */
let installed = false;
export const installToastFilter = () => {
  if (installed) return;
  installed = true;

  const originalError = toast.error.bind(toast);

  toast.error = (content, options) => {
    // If the toast already has a toastId, skip dedup (caller handles it)
    const hasToastId = options?.toastId;

    if (shouldSuppressToast(content)) return;
    if (!hasToastId && isDuplicate(content)) return;

    return originalError(content, options);
  };
};
